import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  message,
  Row,
  Col,
  Statistic,
  DatePicker,
  Tabs,
  Progress,
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  BarChartOutlined,
  LineChartOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';
import { Line } from '@ant-design/charts';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const SalesRevenue = () => {
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [commissionModalVisible, setCommissionModalVisible] = useState(false);
  const [commissionForm] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch sales analytics and orders
  const {
    data: salesData = [],
    isLoading,
    refetch,
  } = useQuery(
    ['sales-analytics', dateRange, searchTerm, statusFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (dateRange && dateRange.length === 2) {
          params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
          params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
        }
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter) params.append('status', statusFilter);
        params.append('per_page', '100');

        // Fetch orders with commission data
        const ordersResponse = await api.get(`/admin/orders?${params.toString()}`);
        let orders = ordersResponse.data?.data || ordersResponse.data || [];

        // Filter out completed orders
        orders = orders.filter((order) => order.order_status !== 'completed');

        // Calculate analytics from orders
        const totalRevenue = orders.reduce(
          (sum, order) => sum + parseFloat(order.total_amount || 0),
          0,
        );
        const totalOrders = orders.length;
        const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

        // Calculate real growth rate (compare with previous period)
        const now = dayjs();
        const thirtyDaysAgo = now.subtract(30, 'day');
        const sixtyDaysAgo = now.subtract(60, 'day');

        const recentOrders = orders.filter((order) =>
          dayjs(order.created_at).isAfter(thirtyDaysAgo),
        );
        const previousOrders = orders.filter(
          (order) =>
            dayjs(order.created_at).isAfter(sixtyDaysAgo) &&
            dayjs(order.created_at).isBefore(thirtyDaysAgo),
        );

        const recentRevenue = recentOrders.reduce(
          (sum, order) => sum + parseFloat(order.total_amount || 0),
          0,
        );
        const previousRevenue = previousOrders.reduce(
          (sum, order) => sum + parseFloat(order.total_amount || 0),
          0,
        );

        const growthRate =
          previousRevenue > 0 ? ((recentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

        // Order status distribution (real counts, not percentages)
        const orderStatusCounts = {
          pending: orders.filter((o) => o.order_status === 'pending').length,
          delivered: orders.filter((o) => o.order_status === 'delivered').length,
          cancelled: orders.filter((o) => o.order_status === 'cancelled').length,
          completed: orders.filter((o) => o.order_status === 'completed').length,
          out_for_delivery: orders.filter((o) => o.order_status === 'out_for_delivery').length,
        };

        // Calculate percentages for display
        const totalStatusOrders = Object.values(orderStatusCounts).reduce(
          (sum, count) => sum + count,
          0,
        );
        const orderStatus = {};
        Object.keys(orderStatusCounts).forEach((status) => {
          orderStatus[status] =
            totalStatusOrders > 0
              ? Math.round((orderStatusCounts[status] / totalStatusOrders) * 100)
              : 0;
        });

        // Generate revenue data for charts (group by date)
        const revenueData = orders
          .reduce((acc, order) => {
            const date = dayjs(order.created_at).format('YYYY-MM-DD');
            const existing = acc.find((item) => item.date === date);
            if (existing) {
              existing.revenue += parseFloat(order.total_amount || 0);
              existing.orders += 1;
            } else {
              acc.push({
                date,
                revenue: parseFloat(order.total_amount || 0),
                orders: 1,
              });
            }
            return acc;
          }, [])
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        // Generate category data from order items
        const categoryData = orders.reduce((acc, order) => {
          if (order.items) {
            order.items.forEach((item) => {
              const category =
                item.product_service?.category?.name ||
                (item.product_service?.type === 'product' ? 'Products' : 'Services') ||
                'General';
              const existing = acc.find((item) => item.category === category);
              if (existing) {
                existing.revenue += parseFloat(item.price_at_order || 0) * item.quantity;
                existing.orders += 1;
              } else {
                acc.push({
                  category,
                  revenue: parseFloat(item.price_at_order || 0) * item.quantity,
                  orders: 1,
                });
              }
            });
          }
          return acc;
        }, []);

        // Calculate time-based order counts
        const today = dayjs().startOf('day');
        const weekStart = dayjs().startOf('week');
        const monthStart = dayjs().startOf('month');

        const ordersToday = orders.filter((order) => dayjs(order.created_at).isAfter(today)).length;
        const ordersThisWeek = orders.filter((order) =>
          dayjs(order.created_at).isAfter(weekStart),
        ).length;
        const ordersThisMonth = orders.filter((order) =>
          dayjs(order.created_at).isAfter(monthStart),
        ).length;

        // Generate top products data
        const topProducts = orders
          .reduce((acc, order) => {
            if (order.items) {
              order.items.forEach((item) => {
                const productName = item.product_service?.name || 'Unknown Product';
                const existing = acc.find((p) => p.name === productName);
                if (existing) {
                  existing.revenue += parseFloat(item.price_at_order || 0) * item.quantity;
                  existing.quantity += item.quantity;
                } else {
                  acc.push({
                    name: productName,
                    revenue: parseFloat(item.price_at_order || 0) * item.quantity,
                    quantity: item.quantity,
                  });
                }
              });
            }
            return acc;
          }, [])
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5);

        return {
          total_revenue: totalRevenue,
          total_orders: totalOrders,
          average_order_value: averageOrderValue,
          growth_rate: growthRate,
          order_status: orderStatus,
          order_status_counts: orderStatusCounts,
          revenue_data: revenueData,
          category_data: categoryData,
          top_products: topProducts,
          orders_today: ordersToday,
          orders_week: ordersThisWeek,
          orders_month: ordersThisMonth,
          conversion_rate: 85.2, // Mock data - could be calculated from real data
          customer_retention: 92.1, // Mock data - could be calculated from real data
          orders: orders.map((order) => ({
            ...order,
            order_number: `ORD-${order.id}`,
            customer_name: order.customer?.name || order.customer?.full_name || 'Unknown Customer',
            provider_name: order.provider?.name || order.provider?.full_name || 'Unknown Provider',
            commission_rate: order.commission_rate || 0,
            commission_amount: order.commission_amount || 0,
          })),
        };
      } catch (error) {
        console.error('Failed to fetch sales analytics:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 60000, // Refresh every minute
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
  );

  // Set commission mutation
  const setCommissionMutation = useMutation(
    async ({ orderId, commissions }) => {
      const response = await api.post(`/admin/orders/${orderId}/set-commission`, {
        commissions: commissions,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Commission set successfully');
        setCommissionModalVisible(false);
        commissionForm.resetFields();
        refetch();
      },
      onError: (error) => {
        message.error(
          'Failed to set commission: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const handleCommissionSubmit = () => {
    commissionForm
      .validateFields()
      .then((values) => {
        // Calculate commission amounts for each item
        const commissions = values.commissions.map((item) => ({
          order_item_id: item.order_item_id,
          commission_rate: item.commission_rate,
        }));

        setCommissionMutation.mutate({
          orderId: selectedOrder.id,
          commissions: commissions,
        });
      })
      .catch((error) => {
        console.error('Validation failed:', error);
      });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries('sales-analytics');
  };

  const handleExport = () => {
    message.info('Export functionality will be implemented soon');
  };

  // Prepare data for charts using real data
  const revenueByMonth =
    salesData.revenue_data?.reduce((acc, item) => {
      const month = dayjs(item.date).format('YYYY-MM');
      const existing = acc.find((a) => a.month === month);
      if (existing) {
        existing.revenue += item.revenue;
        existing.orders += item.orders;
      } else {
        acc.push({ month, revenue: item.revenue, orders: item.orders });
      }
      return acc;
    }, []) || [];

  const revenueByCategory = salesData.category_data || [];
  const pieData = revenueByCategory.map((item) => ({
    type: item.category,
    value: item.revenue,
  }));

  // Debug: Log the data structure

  const revenueColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('MMM DD, YYYY'),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: 'Orders',
      dataIndex: 'orders',
      key: 'orders',
      sorter: (a, b) => a.orders - b.orders,
      render: (orders) => <span style={{ color: '#1890ff', fontWeight: '500' }}>{orders}</span>,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      sorter: (a, b) => a.revenue - b.revenue,
      render: (revenue) => (
        <span
          style={{
            fontWeight: '600',
            color: revenue > 0 ? '#52c41a' : '#666',
          }}
        >
          ₨{parseFloat(revenue || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Average Order Value',
      key: 'average_order_value',
      sorter: (a, b) => a.revenue / a.orders - b.revenue / b.orders,
      render: (_, record) => {
        const avgOrderValue = record.orders > 0 ? record.revenue / record.orders : 0;
        return (
          <span
            style={{
              fontWeight: '500',
              color: avgOrderValue > 0 ? '#722ed1' : '#666',
            }}
          >
            ₨{parseFloat(avgOrderValue || 0).toFixed(2)}
          </span>
        );
      },
    },
  ];

  const handleSetCommission = (order) => {
    setSelectedOrder(order);
    // Initialize commission form with existing commission data
    const initialCommissions =
      order.items?.map((item) => ({
        order_item_id: item.id,
        product_name: item.product_service?.name || 'Unknown Product',
        quantity: item.quantity,
        price: item.price_at_order,
        total: item.quantity * item.price_at_order,
        commission_rate: item.commission_rate || item.product_service?.commission_rate || 0,
        commission_amount: item.commission_amount || 0,
      })) || [];

    commissionForm.setFieldsValue({
      commissions: initialCommissions,
    });
    setCommissionModalVisible(true);
  };

  return (
    <div>
      <Title level={2}>
        <DollarOutlined /> Sales & Revenue Analytics
      </Title>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={salesData.total_revenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={salesData.total_orders || 0}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Average Order Value"
              value={salesData.average_order_value || 0}
              prefix={<BarChartOutlined />}
              precision={2}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Growth Rate"
              value={salesData.growth_rate || 0}
              prefix={<RiseOutlined />}
              suffix="%"
              precision={1}
              valueStyle={{
                color: (salesData.growth_rate || 0) >= 0 ? '#52c41a' : '#ff4d4f',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
              value={dateRange}
              onChange={setDateRange}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="completed">Completed</Option>
              <Option value="pending">Pending</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={4} style={{ textAlign: 'right' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={isLoading}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <BarChartOutlined /> Overview
            </span>
          }
          key="overview"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card title="Revenue Trend" extra={<Button type="text">Last 30 Days</Button>}>
                <Line
                  data={revenueByMonth}
                  xField="month"
                  yField="revenue"
                  height={300}
                  point={{
                    size: 5,
                    shape: 'diamond',
                  }}
                  smooth
                  color="#1890ff"
                />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Revenue by Category">
                <div
                  style={{
                    height: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {pieData.length > 0 ? (
                    pieData.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: '10px',
                          padding: '8px',
                          background: '#f0f0f0',
                          borderRadius: '4px',
                          width: '100%',
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>{item.type}</div>
                        <div style={{ color: '#1890ff' }}>Rs. {item.value.toFixed(2)}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#999' }}>No category data available</div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card title="Top Products">
                <div
                  style={{
                    height: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {salesData.top_products?.length > 0 ? (
                    salesData.top_products.map((product, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: '10px',
                          padding: '8px',
                          background: '#f0f0f0',
                          borderRadius: '4px',
                          width: '100%',
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>{product.name}</div>
                        <div style={{ color: '#52c41a' }}>Rs. {product.revenue.toFixed(2)}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#999' }}>No product data available</div>
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Sales Performance">
                <div style={{ padding: '20px 0' }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '26px', fontWeight: '600', color: '#1890ff' }}>
                          {salesData.conversion_rate || 0}%
                        </div>
                        <div style={{ color: '#666', marginTop: '8px' }}>Conversion Rate</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '26px', fontWeight: '600', color: '#52c41a' }}>
                          {salesData.customer_retention || 0}%
                        </div>
                        <div style={{ color: '#666', marginTop: '8px' }}>Customer Retention</div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <LineChartOutlined /> Revenue Details
            </span>
          }
          key="revenue"
        >
          <Card>
            <Table
              columns={revenueColumns}
              dataSource={salesData.revenue_data || []}
              rowKey="date"
              loading={isLoading}
              pagination={{
                total: salesData.revenue_data?.length || 0,
                pageSize: 15,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <CalendarOutlined /> Order Analytics
            </span>
          }
          key="orders"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <Card>
                <Statistic
                  title="Orders Today"
                  value={salesData.orders_today || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card>
                <Statistic
                  title="Orders This Week"
                  value={salesData.orders_week || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card>
                <Statistic
                  title="Orders This Month"
                  value={salesData.orders_month || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Order Status Distribution" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="circle"
                    percent={salesData.order_status?.completed || 0}
                    format={(percent) => `${percent}%`}
                    strokeColor="#52c41a"
                  />
                  <div style={{ marginTop: '8px', fontWeight: '500' }}>Completed</div>
                  <div style={{ color: '#666' }}>
                    {salesData.order_status_counts?.completed || 0} orders
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="circle"
                    percent={salesData.order_status?.pending || 0}
                    format={(percent) => `${percent}%`}
                    strokeColor="#fa8c16"
                  />
                  <div style={{ marginTop: '8px', fontWeight: '500' }}>Pending</div>
                  <div style={{ color: '#666' }}>
                    {salesData.order_status_counts?.pending || 0} orders
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="circle"
                    percent={salesData.order_status?.cancelled || 0}
                    format={(percent) => `${percent}%`}
                    strokeColor="#ff4d4f"
                  />
                  <div style={{ marginTop: '8px', fontWeight: '500' }}>Cancelled</div>
                  <div style={{ color: '#666' }}>
                    {salesData.order_status_counts?.cancelled || 0} orders
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="circle"
                    percent={salesData.order_status?.delivered || 0}
                    format={(percent) => `${percent}%`}
                    strokeColor="#722ed1"
                  />
                  <div style={{ marginTop: '8px', fontWeight: '500' }}>Delivered</div>
                  <div style={{ color: '#666' }}>
                    {salesData.order_status_counts?.delivered || 0} orders
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <DollarOutlined /> Commission Management
            </span>
          }
          key="commission"
        >
          <Card title="Set Order Commission" style={{ marginBottom: '16px' }}>
            <Table
              columns={[
                {
                  title: 'Order ID',
                  dataIndex: 'order_number',
                  key: 'order_number',
                },
                {
                  title: 'Customer',
                  dataIndex: 'customer_name',
                  key: 'customer_name',
                },
                {
                  title: 'Provider',
                  dataIndex: 'provider_name',
                  key: 'provider_name',
                },
                {
                  title: 'Items',
                  dataIndex: 'items_count',
                  key: 'items_count',
                  render: (count, record) => record.items?.length || 0,
                },
                {
                  title: 'Total Amount',
                  dataIndex: 'total_amount',
                  key: 'total_amount',
                  render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
                },
                {
                  title: 'Order Status',
                  dataIndex: 'order_status',
                  key: 'order_status',
                  render: (status) => {
                    const colors = {
                      pending: 'orange',
                      confirmed: 'blue',
                      preparing: 'purple',
                      ready: 'cyan',
                      on_the_way: 'geekblue',
                      out_for_delivery: 'geekblue',
                      delivered: 'green',
                      cancelled: 'red',
                      completed: 'purple',
                    };
                    return (
                      <Tag color={colors[status]}>
                        {status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                      </Tag>
                    );
                  },
                },
                {
                  title: 'Commission Status',
                  dataIndex: 'commission_amount',
                  key: 'commission_amount',
                  render: (amount, record) => {
                    const hasCommission = record.items?.some((item) => item.commission_rate > 0);
                    return hasCommission ? (
                      <Tag color="green">Set</Tag>
                    ) : (
                      <Tag color="orange">Not Set</Tag>
                    );
                  },
                },
                {
                  title: 'Total Commission',
                  dataIndex: 'commission_amount',
                  key: 'commission_amount',
                  render: (amount, record) => {
                    const totalCommission =
                      record.items?.reduce(
                        (sum, item) => sum + parseFloat(item.commission_amount || 0),
                        0,
                      ) || 0;
                    return `Rs. ${totalCommission.toFixed(2)}`;
                  },
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_, record) => {
                    // Orders that need regular commission setting
                    const statusesThatDontNeedCommission = ['delivered', 'cancelled', 'completed'];
                    const needsCommission = !statusesThatDontNeedCommission.includes(
                      record.order_status,
                    );

                    return (
                      <Space>
                        {needsCommission ? (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleSetCommission(record)}
                            title="Set commission for this order"
                          >
                            Set Commission
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => handleSetCommission(record)}
                            title={`Correct commission for ${record.order_status?.replace('_', ' ').toUpperCase()} order`}
                          >
                            Correct Commission
                          </Button>
                        )}
                      </Space>
                    );
                  },
                },
              ]}
              dataSource={salesData.orders || []}
              rowKey="id"
              loading={isLoading}
              pagination={{
                total: salesData.orders?.length || 0,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
              }}
              scroll={{ x: 1200 }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ margin: 0 }}>
                    <h4>Order Items:</h4>
                    <Table
                      columns={[
                        {
                          title: 'Product',
                          dataIndex: ['product_service', 'name'],
                          key: 'product_name',
                        },
                        {
                          title: 'Quantity',
                          dataIndex: 'quantity',
                          key: 'quantity',
                        },
                        {
                          title: 'Price',
                          dataIndex: 'price_at_order',
                          key: 'price_at_order',
                          render: (price) => `Rs. ${parseFloat(price || 0).toFixed(2)}`,
                        },
                        {
                          title: 'Total',
                          key: 'total',
                          render: (_, record) =>
                            `Rs. ${(record.quantity * record.price_at_order).toFixed(2)}`,
                        },
                        {
                          title: 'Commission %',
                          dataIndex: 'commission_rate',
                          key: 'commission_rate',
                          render: (rate) => `${rate || 0}%`,
                        },
                        {
                          title: 'Commission Amount',
                          dataIndex: 'commission_amount',
                          key: 'commission_amount',
                          render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
                        },
                      ]}
                      dataSource={record.items || []}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                  </div>
                ),
                rowExpandable: (record) => record.items && record.items.length > 0,
              }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Commission Modal */}
      <Modal
        title={`${selectedOrder && ['delivered', 'cancelled', 'completed'].includes(selectedOrder.order_status) ? 'Correct' : 'Set'} Commission - Order ${selectedOrder?.order_number || ''}`}
        open={commissionModalVisible}
        onCancel={() => setCommissionModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCommissionModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={setCommissionMutation.isLoading}
            onClick={handleCommissionSubmit}
          >
            {selectedOrder &&
            ['delivered', 'cancelled', 'completed'].includes(selectedOrder.order_status)
              ? 'Correct Commission'
              : 'Set Commission'}
          </Button>,
        ]}
        width={800}
      >
        <Form form={commissionForm} layout="vertical">
          <Form.List name="commissions">
            {(fields, { add: _add, remove: _remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      marginBottom: '16px',
                      padding: '16px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                    }}
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'product_name']} label="Product">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...restField} name={[name, 'quantity']} label="Quantity">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...restField} name={[name, 'total']} label="Total (Rs.)">
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'commission_rate']}
                          label="Commission %"
                          rules={[
                            { required: true, message: 'Please enter commission rate' },
                            {
                              type: 'number',
                              min: 0,
                              max: 100,
                              message: 'Commission rate must be between 0 and 100',
                            },
                          ]}
                        >
                          <InputNumber
                            min={0}
                            max={100}
                            formatter={(value) => `${value}%`}
                            parser={(value) => value.replace('%', '')}
                            placeholder="0%"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          {...restField}
                          name={[name, 'order_item_id']}
                          style={{ display: 'none' }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesRevenue;
